/**
 * Factory for creating GLB mesh processors, the GLB slice's loader-facing
 * adapter (`resources/formats/glb/`, ADR-0031). Stays here because the
 * `ResourceLoader` constructs it alongside its peer factories.
 */
import * as THREE from 'three';
import { createResourceProcessor } from '../createResourceProcessor';
import { createGLBMesh, tagImportMaterial } from '../formats/glb/glbProcessing';
import { forEachSurfaceMaterial } from '../formats/glb/surfaceMaterials';
import { isGltfPath } from '../../godot/import/gltf';
import { gltfResourcePath, selfContainedGlb } from '../formats/glb/gltfResources';
import { importedClip } from '../../godot/import/animationImport.js';
import { importedLoopMode, setImportedLoopMode } from '../formats/glb/importedLoopMode';
import { AnimationLoopMode } from '../../godot/scene/animation.js';
import { applyRootScale } from '../formats/glb/rootScale';
import { stampMeshInstanceLayers } from '../formats/glb/meshInstances';
import { flattenGlbObjects } from '../../r3f/internal/glb-scene-root/glbHierarchy';
import { matchGlbNode } from '../../r3f/internal/glb-scene-root/glbPathLookup';
import { importClipOptions, importExternalMaterials, importGltfNaming, importLightmapUnwrap, importNodeLayers, importRootScale, importTextureOptions, } from '../../parser/importParser';
import { unwrapCachePath } from '../../godot/project/resourceFormats.js';
import { gltfImageFiles, gltfImageSourceFormat, } from '../../godot/import/gltfImageFiles.js';
import { readRoughnessNormal } from './readRoughnessNormal';
import { readImportSidecar } from './readImportSidecar';
import { fileBaseName } from '../../godot/string/string.js';
import * as logger from '../../logger';
/**
 * Dispose of a GLB mesh and all its resources. Unlike a per-consumer clone
 * (whose geometry is shared with this template, see
 * `disposeClonedMaterials`), the template owns its geometry too.
 */
function disposeGLBMesh(mesh) {
    // Materials through the same slot-gated walker the sidecar writes through, so a
    // surface it can reach is a surface this can free.
    forEachSurfaceMaterial(mesh, (material) => material.dispose());
    mesh.traverse((node) => {
        if (node instanceof THREE.Mesh)
            node.geometry?.dispose();
    });
}
/** The glTF at `path` as the reader of every file its load reads. */
function glbReader(path) {
    return { busType: 'glb', key: path };
}
/**
 * The glTF at `path` as a GLB that carries its external buffers and images: each is read
 * through the byte layer and recorded, so editing it reloads the glTF.
 */
function glbBytes(path, data, fileEventBus, dependencies) {
    return selfContainedGlb(data, async (uri) => {
        const resourcePath = gltfResourcePath(path, uri);
        if (resourcePath === null) {
            throw new Error(`glTF ${path} needs ${uri}, which climbs above res://`);
        }
        dependencies.record(glbReader(path), resourcePath);
        // The packed GLB holds the bytes, so the bus need not.
        const bytes = await fileEventBus?.readOnce(resourcePath, 'GltfResource');
        if (!(bytes instanceof ArrayBuffer)) {
            throw new Error(`glTF ${path} needs ${resourcePath}, which did not load as bytes`);
        }
        return bytes;
    });
}
/**
 * The lightmap UV2 the sidecar asks for, with the asset's **Unwrap cache**, or undefined when it
 * unwraps no mesh. Only then is the cache read: it is found by convention, and absent is ordinary.
 */
async function readLightmapUv(path, sidecar, fileEventBus, dependencies) {
    const unwrap = importLightmapUnwrap(sidecar);
    if (!unwrap || !fileEventBus)
        return undefined;
    const cachePath = unwrapCachePath(path);
    dependencies.record(glbReader(path), cachePath);
    // The rebuilt meshes hold what they need, so the bus need not keep the bytes.
    const cache = await fileEventBus.readOnce(cachePath, 'UnwrapCache');
    return {
        unwrap,
        cache: cache instanceof ArrayBuffer ? cache : null,
        rootScale: importRootScale(sidecar),
        path,
    };
}
/**
 * How Godot imports each image of the glTF at `path`: its bytes' source format under the sidecar of
 * the file it loads. An external image loads through its own import (`gltf_document.cpp:2356-2363`).
 * An embedded one imports as the file Godot extracts it to, with mipmaps (`:2251-2255`). Each sidecar
 * read is recorded, so an edit to it reloads the asset. Null when no loader header names a format.
 */
function importedImages(path, images, fileEventBus, dependencies) {
    const files = gltfImageFiles(path, images);
    const reader = glbReader(path);
    return async (index, bytes, mimeType) => {
        const source = gltfImageSourceFormat(bytes, mimeType);
        const file = files[index];
        if (source === null || file === undefined)
            return null;
        // A URI above res:// names no project file, so Godot extracts the image (`gltf_document.cpp:2203`).
        const externalPath = file.uri === null ? null : gltfResourcePath(path, file.uri);
        const imagePath = externalPath ?? file.extracted;
        const read = { assetPath: imagePath, dependent: reader, fileEventBus, dependencies };
        const sidecar = await readImportSidecar(read);
        const sidecarOptions = importTextureOptions(sidecar);
        const options = externalPath === null ? { ...sidecarOptions, mipmaps: true } : sidecarOptions;
        const roughnessNormal = await readRoughnessNormal({ ...read, options });
        return { source, options, roughnessNormal };
    };
}
/**
 * Correct a loaded asset by its sidecar, once per path on the cached template, so render, bounds,
 * selection and the tree see one object. Godot's importer too writes each correction into the asset.
 */
function applyImportSidecar(object, path, parsed) {
    if (!parsed)
        return;
    applySidecarRootScale(object, path, parsed);
    applySidecarNodeLayers(object, path, parsed);
    tagSidecarMaterials(object, path, parsed);
}
/** `nodes/root_scale`, applied to the asset the way `nodes/apply_root_scale` asks. */
function applySidecarRootScale(object, path, parsed) {
    const rootScale = importRootScale(parsed);
    if (!rootScale)
        return;
    logger.info(`[GLBProcessor] ${path}: import sidecar root_scale ${rootScale.scale} ` +
        `(${rootScale.bake ? 'baked into the asset' : 'on the root node'})`);
    applyRootScale(object, rootScale);
}
/**
 * `_subresources`' per-node `mesh_instance/layers`, matched by node path
 * (`resource_importer_scene.cpp:1836`) through `matchGlbNode`.
 */
function applySidecarNodeLayers(object, path, parsed) {
    const masks = importNodeLayers(parsed);
    if (masks.size === 0)
        return;
    const entries = flattenGlbObjects(object);
    for (const [nodePath, mask] of masks) {
        const target = matchGlbNode(entries, nodePath);
        if (!target) {
            logger.warn(`[GLBProcessor] ${path}: import sidecar layers path '${nodePath}' names no mesh`);
            continue;
        }
        logger.info(`[GLBProcessor] ${path}: import sidecar layers ${mask} on '${nodePath}'`);
        stampMeshInstanceLayers(target.object, mask);
    }
}
/**
 * `_subresources`' external materials, matched to surfaces by glTF material name:
 * Godot's `mat->get_meta("import_id", mat->get_name())` key
 * (`editor/import/3d/resource_importer_scene.cpp:1583`). Each matched surface is tagged
 * with its `.tres` address and keeps the glTF's own material, which an unresolvable
 * `.tres` leaves in place, as Godot's null `external_mat` branch does (`:1622-1636`).
 */
function tagSidecarMaterials(object, path, parsed) {
    const remaps = importExternalMaterials(parsed);
    if (remaps.size === 0)
        return;
    const tagged = new Map();
    forEachSurfaceMaterial(object, (material) => {
        const external = remaps.get(material.name);
        if (external === undefined)
            return;
        tagImportMaterial(material, external);
        tagged.set(material.name, external);
    });
    if (tagged.size === 0)
        return;
    logger.info(`[GLBProcessor] ${path}: import sidecar external materials ` +
        [...tagged].map(([name, external]) => `${name} -> ${external}`).join(', '));
}
/**
 * Each embedded clip as Godot's scene importer imports it after the glTF importer named it: its
 * final name and loop mode, written on the cached template's clip. It runs with no sidecar too,
 * because the name suffix rule needs none.
 */
function applyImportedClips(object, parsed) {
    const options = importClipOptions(parsed);
    for (const clip of object.animations) {
        const loops = importedLoopMode(clip) === AnimationLoopMode.LINEAR;
        const imported = importedClip({ name: clip.name, loops }, options);
        clip.name = imported.name;
        setImportedLoopMode(clip, imported.loopMode);
    }
}
/**
 * Create a GLB mesh processor that handles loading and caching GLB/GLTF meshes.
 */
export function createGLBProcessor(fileEventBus, eventBus, 
/** Records the asset's read of its sidecar, so a sidecar edit reloads the asset. */
dependencies, extensionRules) {
    return createResourceProcessor({
        fileEventBus,
        eventBus,
        resourceType: 'glb',
        readsFile: isGltfPath,
        shouldProcess: (_path, data) => data instanceof ArrayBuffer,
        process: async (path, data) => {
            const { glb, images } = await glbBytes(path, data, fileEventBus, dependencies);
            const sidecar = await readImportSidecar({
                assetPath: path,
                dependent: glbReader(path),
                fileEventBus,
                dependencies,
            });
            const object = await createGLBMesh(glb, {
                extensionRules,
                naming: { ...importGltfNaming(sidecar), fileName: fileBaseName(path) },
                lightmapUv: await readLightmapUv(path, sidecar, fileEventBus, dependencies),
                imageImport: importedImages(path, images, fileEventBus, dependencies),
            });
            applyImportSidecar(object, path, sidecar);
            applyImportedClips(object, sidecar);
            return object;
        },
        dispose: disposeGLBMesh,
    });
}
//# sourceMappingURL=createGLBProcessor.js.map